import fp from 'lodash/fp';
import React, { ComponentType, ComponentProps, useCallback } from 'react';
import { formatDate } from '@factorin/util';

import {
    cn, Button, Text, Heading,
    ProgressLoader,
    ThreeDots, Document, addChildrenProps, ClassName,
} from 'Modules/ui';

import { PopupWithButton } from '../PopupWithButton';
import styles from './index.module.scss';
import { DownloadPopupProps, FileItemProps, FileSectionProps } from './types';


export const DownloadPopup: ComponentType<DownloadPopupProps> = ({
    classNames = {},
    isDownloading,
    icon: Icon = ThreeDots,
    ...props
}) => {
    const renderButton = useCallback(
        ({ show, className }) => (
            <Button
                variant='auxiliary'
                className={cn(styles.button, classNames.button, className)}
                onClick={show}
            >
                {isDownloading
                    ? <ProgressLoader className={styles.progressLoader} indeterminate />
                    : <Icon />
                }
            </Button>
        ),
        [Icon, classNames.button, isDownloading],
    );

    return (
        <PopupWithButton
            classNames={{
                ...classNames,
                container: cn(styles.downloadPopup, classNames.container),
                popup: cn(styles.popup, classNames.popup),
                content: cn(styles.content, classNames.content),
                popupLocation: {
                    top: styles.top,
                    right: styles.right,
                    bottom: styles.bottom,
                    left: styles.left,
                },
            }}
            button={renderButton}
            {...props}
        />
    );
};

export const SectionHeader: ComponentType<ClassName> = ({ children, className }) => (
    <Heading
        headingType='h1Element'
        component='h1'
        className={cn(styles.sectionHeading, className)}
    >
        {children}
    </Heading>
);

export const InfoSection: ComponentType = ({ children }) => (
    <Text textType='info' component='div' className={styles.infoSection}>
        {children}
    </Text>
);

export const FileSection: ComponentType<FileSectionProps> = ({
    className,
    children,
    ratios = [1, 1],
}) => (
    <div className={cn(styles.fileSection, className)}>
        {addChildrenProps(children, { ratios })}
    </div>
);

export const FileItem: ComponentType<FileItemProps> = ({
    title, description, date,
    onDownload, isNotDownloadable, progress, button: ItemButton,
    icon: Icon = Document,
    ratios: [titleRatio, descriptionRatio],
}) => (
    <div className={styles.fileItem}>
        <div className={cn(styles.fileItemPart, styles.icon)}>
            {progress
                ? <ProgressLoader className={styles.progressLoader} {...progress} />
                : <Icon />
            }
        </div>
        {title && (
            <div
                className={cn(styles.fileItemPart, styles.download)}
                title={title as string}
                style={{ flex: titleRatio }}
            >
                <Text<ComponentProps<'a'>>
                    component='a'
                    textType={!isNotDownloadable ? 'link' : 'regular'}
                    onClick={!isNotDownloadable ? onDownload : fp.F}
                >
                    {title}
                </Text>
            </div>
        )}
        {description && (
            <div
                title={description as string}
                className={cn(styles.fileItemPart, styles.description)}
                style={{ flex: descriptionRatio }}
            >
                <Text<ComponentProps<'span'>> textType='light' ellipsis component='span'>
                    {description}
                </Text>
            </div>
        )}
        {date && (
            <Text
                textType='light'
                component='div'
                className={cn(styles.fileItemPart, styles.date)}
            >
                {formatDate(date)}
            </Text>
        )}
        {ItemButton && <ItemButton className={cn(styles.fileItemPart, styles.icon)} />}
    </div>
);

export * from './types';
